// One authored change to the selected App (a refresh time, a renewed source) carried through the EXISTING finish
// chain: prepare (the sources/refresh route) -> prepareFinish -> finish -> the exact saved readVersion -> open the
// new saved version. Only the writes keep their original keys: the command's operation id is minted once and kept
// until the change is saved or definitely refused, and the prepared original is reused, so a retry replays that
// original and never becomes a second change. A lost finish reply is reconciled from the original receipt
// ("Check again"). Reads are plain.
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useOptionalWorkspace } from '@/workspace/context';
import { SessionChangedError } from '@/lib/session-guard';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import {
  finishCandidateReferenceSchema,
  type FinishArtifactInput,
  type FinishCandidateReference,
} from '../../../../src/artifacts/authoring/finish-contract.mjs';
import { savedArtifactViewSchema } from '../../../../src/artifacts/saved-view-contract.mjs';
import { artifactApi } from '../../artifacts/api';
import { createArtifactFinishActions } from '../../artifacts/finish-actions';
import { savedArtifactCanvasTarget } from '../../artifacts/routes';

/** Retry waits (ms) between attempts at the same-key complete before the person is told it is unconfirmed. */
export const rolloutPolicy = { delays: [500, 1500, 4000] as number[] };

export type CommitPhase =
  | 'idle'
  | 'working'
  | 'saved'
  | 'failed'
  | 'unconfirmed'
  // the saved version is committed but the rollout is still unfinished after the retries; Check again re-asks the same complete
  | 'completion-unknown';

type Completion = { versionId: string; revisionId: string; operationId: string };
/** What follows a COMMITTED refresh save (S07): the rollout completion, and its read-only recovery. */
export type AfterSave = {
  complete(input: Completion): Promise<{ saved: { versionId: string } }>;
  result(input: Completion): Promise<{ saved: { versionId: string } } | null>;
};

export function useSourceCommit() {
  const workspace = useOptionalWorkspace();
  const session = useRef(useSessionBoundFetch()).current;
  const actions = useMemo(
    () =>
      createArtifactFinishActions({
        finish: (input) => artifactApi.finish(input, session),
        lookupFinishReceipt: (input) => artifactApi.lookupFinishReceipt(input, session),
      }),
    [session],
  );
  const [phase, setPhase] = useState<CommitPhase>('idle');
  const operationId = useRef<string | null>(null);
  const reference = useRef<FinishCandidateReference | null>(null);
  const original = useRef<FinishArtifactInput | null>(null);
  const working = useRef(false);
  const after = useRef<AfterSave | null>(null);
  const completion = useRef<Completion | null>(null);
  const alive = useRef(true);
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const set = (next: CommitPhase) => {
    if (alive.current) setPhase(next);
  };
  const reset = () => {
    operationId.current = null;
    reference.current = null;
    original.current = null;
  };

  const open = (view: {
    artifactId: string;
    revisionId: string;
    versionId: string;
    number: number;
  }) => {
    const target = savedArtifactCanvasTarget({
      artifactId: view.artifactId,
      revisionId: view.revisionId,
      versionId: view.versionId,
      versionNumber: view.number,
      purpose: 'editor',
    });
    reset();
    completion.current = null;
    if (alive.current && target && workspace) void workspace.openCanvas(target);
  };
  const opening = useRef<Parameters<typeof open>[0] | null>(null);

  // The refresh rollout (S07): `complete` is IDEMPOTENT under the ORIGINAL prepare key (the same grant and cell
  // writes), so a lost or failed reply is recovered by calling the SAME complete again, never by a new key, a
  // new save or a user step. The read-only result may OBSERVE an already-finished rollout first; when it has
  // nothing, the same complete finishes it. Only after the retries are spent does the person see the existing
  // "couldn't confirm" line, whose Check again runs the same loop once more.
  const finishRollout = async () => {
    const c = completion.current;
    const view = opening.current;
    const hook = after.current;
    if (!c || !view || !hook) return;
    set('working');
    const settle = (ready: { saved: { versionId: string } } | null) => {
      if (!ready) return false;
      // retired while the call was in flight (another selection, session or screen): nothing is shown or opened
      if (!alive.current) return true;
      if (ready.saved.versionId !== view.versionId) {
        set('failed'); // not this saved version
        return true;
      }
      set('saved');
      open(view);
      return true;
    };
    for (let attempt = 0; attempt <= rolloutPolicy.delays.length; attempt++) {
      if (attempt > 0)
        await new Promise((resolve) => setTimeout(resolve, rolloutPolicy.delays[attempt - 1]));
      if (!alive.current) return;
      try {
        if (settle(await hook.complete(c))) return;
      } catch (error) {
        if (error instanceof SessionChangedError || !alive.current) return;
        try {
          if (settle(await hook.result(c))) return; // observed as already finished
        } catch (observed) {
          if (observed instanceof SessionChangedError) return;
        }
      }
    }
    if (alive.current) set('completion-unknown');
  };

  // The exact saved read of what the commit named, then (for a refresh) the rollout, then open. A failed read
  // leaves the saved change saved.
  const confirm = async (versionId: string, artifactId: string) => {
    set('saved');
    const key = operationId.current;
    try {
      const view = savedArtifactViewSchema.parse(
        await artifactApi.readVersion({ versionId, purpose: 'editor' }, session),
      );
      if (!alive.current || view.artifactId !== artifactId || view.versionId !== versionId) return;
      if (after.current && key) {
        opening.current = view;
        completion.current = {
          versionId: view.versionId,
          revisionId: view.revisionId,
          operationId: key,
        };
        await finishRollout();
      } else open(view);
    } catch {
      // saved either way; the Library/Versions show it
    }
  };

  /** `prepare(operationId)` is the sources/refresh route call for THIS command. */
  const run = useCallback(
    async (prepare: (operationId: string) => Promise<unknown>, followUp?: AfterSave) => {
      if (working.current) return;
      after.current = followUp ?? null;
      working.current = true;
      set('working');
      try {
        operationId.current ??= crypto.randomUUID();
        if (!reference.current) {
          try {
            reference.current = finishCandidateReferenceSchema.parse(
              await prepare(operationId.current),
            );
          } catch (error) {
            if (error instanceof SessionChangedError) return;
            return set('failed'); // the same operation id replays the original on the next try
          }
        }
        if (!alive.current) return;
        if (!original.current) {
          try {
            original.current = await artifactApi.prepareFinish(reference.current, session);
          } catch (error) {
            if (error instanceof SessionChangedError) return;
            return set('failed');
          }
        }
        if (!alive.current) return;
        let result;
        try {
          result = await actions.finishOnce(original.current);
        } catch (error) {
          if (error instanceof SessionChangedError) return;
          original.current = null;
          return set('failed');
        }
        if (!alive.current) return;
        if (result.status === 'committed')
          await confirm(result.version.versionId, original.current.artifactId);
        else if (result.status === 'uncertain') set('unconfirmed');
        else {
          reset();
          set('failed');
        }
      } finally {
        working.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, session, workspace],
  );

  const checkAgain = useCallback(async () => {
    if (working.current) return;
    if (completion.current) {
      // the save is committed: the SAME complete (same key) is asked again
      working.current = true;
      try {
        await finishRollout();
      } finally {
        working.current = false;
      }
      return;
    }
    if (!original.current) return;
    working.current = true;
    try {
      const { artifactId, operationId: id, inputDigest } = original.current;
      const receipt = (await actions.recoverOriginal({
        artifactId,
        operationId: id,
        inputDigest,
      })) as {
        status?: string;
        version?: { versionId?: string };
      };
      if (!alive.current) return;
      if (receipt.status === 'committed' && receipt.version?.versionId)
        await confirm(receipt.version.versionId, artifactId);
      else set('unconfirmed');
    } catch (error) {
      if (!(error instanceof SessionChangedError)) set('unconfirmed');
    } finally {
      working.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, session, workspace]);

  return { phase, run, checkAgain };
}
